<script setup>
import { computed, ref } from 'vue';
import CarouselPreview from './CarouselPreview.vue';
import TemplateButtonsPreview from './TemplateButtonsPreview.vue';

const props = defineProps({
  template: { type: Object, required: true },
  previewHeader: { type: String, default: '' },
  previewBodyWithHighlight: { type: String, default: '' },
});

const showAllButtonsModal = ref(false);

const currentTime = computed(() => {
  return new Date().toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
  });
});

const getHeaderUrl = () => {
  if (props.template.headerMediaUrl) return props.template.headerMediaUrl;
  if (props.template.headerMediaFile instanceof File)
    return URL.createObjectURL(props.template.headerMediaFile);
  return null;
};
</script>

<template>
  <div class="col-span-1">
    <div class="preview flex flex-col sticky top-4">
      <div class="mb-3 flex items-center justify-between">
        <!-- eslint-disable-next-line vue/no-bare-strings-in-template -->
        <h3 class="text-sm font-semibold">Template Preview</h3>
      </div>
      <div
        class="preview__body flex-grow p-4 rounded-lg overflow-y-auto"
        :class="
          template.language === 'ar' ? '[direction:rtl]' : '[direction:ltr]'
        "
      >
        <div
          v-if="template.format === 'STANDARD'"
          class="rounded-lg shadow p-4 max-w-sm bg-white"
        >
          <div v-if="template.header !== 'NONE'" class="mb-2">
            <div
              v-if="template.header === 'TEXT'"
              class="font-semibold text-lg"
            >
              {{
                previewHeader ||
                template.headerText ||
                'Header text will appear here...'
              }}
            </div>
            <div
              v-else-if="
                ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(template.header)
              "
              class="w-full h-32 bg-gray-200 rounded flex items-center justify-center overflow-hidden"
            >
              <img
                v-if="template.header === 'IMAGE'"
                :src="
                  getHeaderUrl() ||
                  'https://via.assets.so/img.jpg?w=400&h=300&bg=e5e7eb&text=IMAGE&f=png'
                "
                alt=""
                class="w-full h-full object-cover"
              />
              <div
                v-else-if="template.header === 'VIDEO'"
                class="w-full h-full bg-gray-200 rounded flex items-center justify-center overflow-hidden"
              >
                <video
                  v-if="getHeaderUrl()"
                  :src="getHeaderUrl()"
                  class="w-full h-full object-cover"
                  controls
                />
                <img
                  v-else
                  src="https://via.assets.so/img.jpg?w=400&h=300&bg=e5e7eb&text=VIDEO&f=png"
                  alt=""
                />
              </div>
              <img
                v-else-if="template.header === 'DOCUMENT'"
                src="https://via.assets.so/img.jpg?w=400&h=300&bg=e5e7eb&text=DOCUMENT&f=png"
                alt=""
              />
            </div>
          </div>
          <div
            class="text-sm mb-3 whitespace-pre-wrap text-gray-800"
            v-html="previewBodyWithHighlight"
          />
          <div v-if="template.footer" class="text-xs mb-3 text-gray-500">
            {{ template.footer }}
          </div>
          <template v-if="template.buttons && template.buttons.length > 0">
            <TemplateButtonsPreview
              :buttons="template.buttons"
              @show-all-buttons="showAllButtonsModal = true"
            />
            <woot-modal v-model:show="showAllButtonsModal">
              <div class="py-12 px-8">
                <TemplateButtonsPreview
                  :buttons="template.buttons"
                  show-all-buttons
                />
              </div>
            </woot-modal>
          </template>
          <div class="text-xs mt-2 text-gray-400 text-end">
            {{ currentTime }}
          </div>
        </div>
        <div v-else class="flex flex-col gap-2">
          <div class="rounded-lg shadow p-4 max-w-sm bg-white">
            <div
              class="text-sm mb-3 whitespace-pre-wrap text-gray-800"
              v-html="previewBodyWithHighlight"
            />
            <div class="text-xs text-right mt-2 text-gray-400">
              {{ currentTime }}
            </div>
          </div>
          <CarouselPreview :cards="template.cards" />
        </div>
      </div>
    </div>
  </div>
</template>
